import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Server,
  Cpu,
  Zap,
  Droplets,
  Shield,
  Leaf,
  Globe,
  Building2,
  CheckCircle2,
  Clock,
  Users,
  Layers,
  Lock,
  Activity,
  MapPin,
  TrendingUp,
  HardDrive,
  Fan,
  Network,
  Factory,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// ---------------------------------------------------------------------------
// DATA
// ---------------------------------------------------------------------------

const stages = [
  {
    id: "hyperscale",
    num: "01",
    label: "Hyperscale Campus",
    sub: "Purpose-built for the largest workloads on earth.",
    Icon: Server,
    color: "from-blue-600 to-cyan-500",
    lightBg: "bg-blue-50",
    lightBorder: "border-blue-200",
    dotColor: "bg-blue-600",
    summary:
      "Rapid-deployment campuses engineered for hyperscale cloud and AI workloads. Development-ready sites with comprehensive power enablement, strategic utility partnerships, and engineering designed for adaptive scaling without costly retrofits.",
    bullets: [
      "Multiple gigawatts of secured power across strategic markets",
      "Pre-entitled sites with utility partnerships already in place",
      "Build-to-scale delivery — expand without re-engineering",
      "Single-point accountability from site to commissioning",
      "90-day accelerated design-to-deployment on powered sites",
    ],
  },
  {
    id: "gigascale",
    num: "02",
    label: "Gigascale AI Campus",
    sub: "Liquid-cooled infrastructure for the AI era.",
    Icon: Cpu,
    color: "from-violet-600 to-purple-500",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    dotColor: "bg-violet-600",
    summary:
      "Purpose-engineered for ultra-high-density AI and ML training clusters. Delta Flow liquid cooling eliminates the thermal ceiling that stops legacy data centers from supporting next-generation GPU deployments.",
    bullets: [
      "Delta Flow direct-to-chip liquid cooling — no thermal ceiling",
      "50+ kW per rack densities supported natively",
      "Designed for NVIDIA GB200, custom ASICs, and next-gen silicon",
      "Closed-loop water systems — 85% reduction in water consumption",
      "AI-specific power distribution and redundancy architecture",
      "Project Caprock: 540 MW sustainable AI campus in NW Texas",
    ],
  },
  {
    id: "enterprise",
    num: "03",
    label: "Multi-Tenant Enterprise",
    sub: "White-glove colocation for mission-critical workloads.",
    Icon: Building2,
    color: "from-emerald-600 to-teal-500",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    dotColor: "bg-emerald-600",
    summary:
      "Enterprise-grade colocation with the operational rigor and concierge service that regulated industries demand. Full compliance frameworks, dedicated support engineering, and adaptive infrastructure that scales with your business.",
    bullets: [
      "SOC 2 Type II, ISO 27001, HIPAA, PCI-DSS compliance",
      "Dedicated customer success engineering team",
      "Cross-connect fabric to all major cloud on-ramps",
      "N+1 through 2N redundancy configurations",
      "Real-time capacity dashboards and SLA reporting",
    ],
  },
  {
    id: "turnkey",
    num: "04",
    label: "Turnkey Data Halls",
    sub: "Flexible whitespace. Your specs. Our engineering.",
    Icon: Layers,
    color: "from-amber-500 to-orange-500",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    dotColor: "bg-amber-500",
    summary:
      "Pre-engineered data halls that match your exact IT requirements without the timeline or cost of a custom build. Aligned's adaptive infrastructure means the mechanical and electrical systems flex to your workload.",
    bullets: [
      "Configurable power density from 8 kW to 50+ kW per rack",
      "Adaptive cooling — air, liquid, or hybrid within the same hall",
      "Pre-provisioned fiber, power, and cooling infrastructure",
      "Occupancy in weeks, not quarters",
      "In-place densification without facility migration",
    ],
  },
];

const platformPillars = [
  {
    Icon: Droplets,
    color: "from-cyan-500 to-blue-600",
    title: "Delta Flow Cooling",
    body: "Patented liquid cooling technology that eliminates the thermal ceiling. Direct-to-chip cooling supports 50+ kW racks while reducing water consumption by 85%.",
  },
  {
    Icon: Zap,
    color: "from-amber-500 to-orange-500",
    title: "5 GW Power Pipeline",
    body: "Multiple gigawatts of secured power capacity across strategic North American and Latin American markets. Utility partnerships already in place.",
  },
  {
    Icon: Leaf,
    color: "from-emerald-500 to-green-500",
    title: "100% Renewable Match",
    body: "Every megawatt-hour consumed in North America is matched with renewable energy. Not offsets. Not credits. Matched generation, verified annually.",
  },
  {
    Icon: Shield,
    color: "from-red-500 to-rose-500",
    title: "Adaptive Infrastructure",
    body: "Mechanical and electrical systems that reconfigure without downtime. As workloads evolve, the facility adapts in place — no migration required.",
  },
  {
    Icon: Clock,
    color: "from-violet-500 to-purple-500",
    title: "Speed to Market",
    body: "Pre-entitled, development-ready sites with power and fiber already secured. Contract to commissioning faster than any ground-up build.",
  },
  {
    Icon: Lock,
    color: "from-slate-500 to-zinc-600",
    title: "Enterprise Security",
    body: "Multi-layer access control, 24/7 NOC, AI-powered threat monitoring, and compliance frameworks for every major regulatory regime.",
  },
];

const campusLocations = [
  { label: "Northern Virginia", flagship: true, mw: "1,200+" },
  { label: "Phoenix, AZ", flagship: true, mw: "800+" },
  { label: "Dallas, TX", flagship: true, mw: "600+" },
  { label: "Salt Lake City, UT", flagship: false, mw: "400+" },
  { label: "Chicago, IL", flagship: false, mw: "300+" },
  { label: "Atlanta, GA", flagship: false, mw: "200+" },
  { label: "Hillsboro, OR", flagship: false, mw: "200+" },
  { label: "Maryland", flagship: false, mw: "300+" },
  { label: "Ohio", flagship: false, mw: "200+" },
  { label: "NW Texas (Caprock)", flagship: true, mw: "540" },
];

const latamLocations = ["Brazil", "Chile", "Colombia", "Mexico"];

const customerProfiles = [
  {
    Icon: Cpu,
    color: "text-violet-600",
    bg: "bg-violet-50",
    iconBorder: "border-violet-200",
    label: "Hyperscale Cloud",
    blurb: "Multi-hundred megawatt deployments with guaranteed power, cooling, and fiber.",
  },
  {
    Icon: Activity,
    color: "text-blue-600",
    bg: "bg-blue-50",
    iconBorder: "border-blue-200",
    label: "AI / ML Platforms",
    blurb: "Liquid-cooled, high-density infrastructure purpose-built for GPU training clusters.",
  },
  {
    Icon: Factory,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    iconBorder: "border-emerald-200",
    label: "Enterprise IT",
    blurb: "Regulated, compliant, white-glove colocation with adaptive capacity.",
  },
  {
    Icon: Network,
    color: "text-amber-600",
    bg: "bg-amber-50",
    iconBorder: "border-amber-200",
    label: "Network & CDN",
    blurb: "Strategic edge presence with dense cross-connect fabrics and direct cloud on-ramps.",
  },
  {
    Icon: TrendingUp,
    color: "text-rose-600",
    bg: "bg-rose-50",
    iconBorder: "border-rose-200",
    label: "Financial Services",
    blurb: "Ultra-low latency, SOC 2 / PCI-DSS compliance, and DR architectures across diverse markets.",
  },
  {
    Icon: Globe,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    iconBorder: "border-indigo-200",
    label: "Sovereign & Government",
    blurb: "FedRAMP-aligned security, ITAR support, and dedicated compliance engineering.",
  },
];

const byTheNumbers = [
  { value: "85", label: "Data Centers" },
  { value: "5 GW", label: "Power Pipeline" },
  { value: "50", label: "Campuses" },
  { value: "100%", label: "Renewable" },
  { value: "$2.58B", label: "Credit Facility" },
  { value: "540 MW", label: "Project Caprock" },
];

// ---------------------------------------------------------------------------
// SUB-COMPONENTS
// ---------------------------------------------------------------------------

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function StageAccordionCard({ stage, expanded, onToggle }) {
  const { Icon } = stage;

  return (
    <div className="mb-3">
      <button
        onClick={onToggle}
        className={`w-full min-h-[56px] rounded-2xl border p-4 text-left transition-all duration-200 ${
          expanded
            ? "border-zinc-900 bg-zinc-900 text-white shadow-xl dark:border-white dark:bg-white dark:text-zinc-900"
            : "border-zinc-200 bg-white/80 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/70"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`shrink-0 rounded-xl bg-gradient-to-br ${stage.color} p-2.5 text-white shadow-lg`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                expanded ? "opacity-50" : "text-zinc-400"
              }`}
            >
              {stage.num}
            </div>
            <div className="text-[16px] font-semibold leading-tight">
              {stage.label}
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="h-5 w-5 shrink-0 opacity-50" />
          ) : (
            <ChevronDown className="h-5 w-5 shrink-0 opacity-30" />
          )}
        </div>
        <p
          className={`mt-1.5 text-sm leading-relaxed ${
            expanded ? "text-zinc-300 dark:text-zinc-600" : "text-zinc-500"
          }`}
        >
          {stage.sub}
        </p>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="pt-3 pb-1 px-1">
              <Card className="rounded-2xl border-0 bg-gradient-to-br from-white to-zinc-50 shadow-lg dark:from-zinc-900 dark:to-zinc-950">
                <CardContent className="p-5">
                  <p className="mb-5 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
                    {stage.summary}
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {stage.bullets.map((item) => (
                      <div
                        key={item}
                        className={`flex items-start gap-3 rounded-xl border ${stage.lightBorder} ${stage.lightBg} p-3 dark:border-zinc-800 dark:bg-zinc-900/50`}
                      >
                        <div
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${stage.dotColor}`}
                        />
                        <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN EXPORT
// ---------------------------------------------------------------------------

export default function AlignedSalesWallMobile() {
  const [expandedStage, setExpandedStage] = useState(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="px-4 pb-24">

        {/* -- HERO -------------------------------------------------- */}
        <section className="py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge className="mb-4 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
              Adaptive Data Center Infrastructure
            </Badge>
            <h1 className="text-3xl font-bold leading-[1.12] tracking-tight">
              <span>Aligned</span>{" "}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Data Centers
              </span>
            </h1>
            <p className="mt-1 text-lg font-semibold text-zinc-400 tracking-tight">
              Scale without compromise.
            </p>
            <p className="mt-4 text-base leading-7 text-zinc-500 dark:text-zinc-400">
              From multi-tenant enterprise colocation to gigawatt-scale AI
              campuses — adaptive infrastructure engineered to grow with your
              most demanding workloads.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6"
          >
            <Card className="rounded-2xl border-0 bg-zinc-900 text-white shadow-2xl dark:bg-zinc-900">
              <CardContent className="p-6">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
                  Why Aligned
                </p>
                <p className="mb-3 text-sm leading-7 text-zinc-300">
                  AI training clusters and hyperscale cloud demand
                  infrastructure that adapts — mechanically, electrically,
                  and thermally — without downtime or compromise.
                </p>
                <p className="text-sm leading-7 text-zinc-300">
                  85 facilities. 5 gigawatts. 50 campuses. 100% renewable.
                  One partner for the entire Americas.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* -- BY THE NUMBERS ---------------------------------------- */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={1}>At Scale</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              Infrastructure that speaks in gigawatts.
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {byTheNumbers.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-zinc-100 bg-white p-3 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70"
              >
                <div className="text-xl font-bold bg-gradient-to-br from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="mt-0.5 text-[11px] font-semibold text-zinc-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* -- SOLUTIONS --------------------------------------------- */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={2}>Solutions</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              Right-sized for every workload.
            </h2>
            <p className="mt-2 text-sm leading-7 text-zinc-500 dark:text-zinc-400">
              Four deployment models, one adaptive platform. Start where you
              are. Scale without re-engineering.
            </p>
          </div>

          <div>
            {stages.map((stage) => (
              <StageAccordionCard
                key={stage.id}
                stage={stage}
                expanded={expandedStage === stage.id}
                onToggle={() =>
                  setExpandedStage(
                    expandedStage === stage.id ? null : stage.id
                  )
                }
              />
            ))}
          </div>
        </section>

        {/* -- DIFFERENTIATORS --------------------------------------- */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={3}>Competitive Advantage</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              What makes Aligned different.
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            {platformPillars.map((pillar) => {
              const { Icon } = pillar;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Card className="rounded-2xl border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-5">
                      <div
                        className={`mb-4 inline-flex rounded-xl bg-gradient-to-br ${pillar.color} p-3 text-white shadow-lg`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 text-base font-bold text-zinc-900 dark:text-zinc-100">
                        {pillar.title}
                      </h3>
                      <p className="text-sm leading-7 text-zinc-500 dark:text-zinc-400">
                        {pillar.body}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* -- CAMPUS FOOTPRINT -------------------------------------- */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={4}>Campus Footprint</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              50 campuses. Two continents.
            </h2>
          </div>

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
            North America
          </p>
          <div className="flex flex-col gap-2 mb-5">
            {campusLocations.map((loc) => (
              <div
                key={loc.label}
                className={`flex items-center justify-between rounded-xl border p-3 ${
                  loc.flagship
                    ? "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30"
                    : "border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70"
                }`}
              >
                <div className="flex items-center gap-2">
                  <MapPin
                    className={`h-4 w-4 ${
                      loc.flagship ? "text-blue-600" : "text-zinc-400"
                    }`}
                  />
                  <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    {loc.label}
                  </span>
                  {loc.flagship && (
                    <Badge className="rounded-full px-2 py-0.5 text-[9px] bg-blue-600 text-white border-0">
                      Flagship
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-zinc-500">{loc.mw} MW</span>
              </div>
            ))}
          </div>

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
            Latin America
          </p>
          <div className="flex flex-wrap gap-2">
            {latamLocations.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-xs font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400"
              >
                <Globe className="h-3 w-3" />
                {name}
              </span>
            ))}
          </div>
        </section>

        {/* -- CUSTOMER PROFILES ------------------------------------- */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={5}>Built For</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              One platform. Every workload.
            </h2>
          </div>

          {/* Dark AI partner card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="mb-4"
          >
            <Card className="rounded-2xl border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-6">
                <div className="mb-5 inline-flex rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 p-3.5 text-white shadow-xl">
                  <HardDrive className="h-7 w-7" />
                </div>
                <h3 className="mb-3 text-xl font-bold">
                  The AI Infrastructure Partner
                </h3>
                <p className="mb-3 text-sm leading-7 text-zinc-300">
                  The largest AI training runs require liquid cooling at rack
                  scale, power density above 50 kW, and hundreds of megawatts
                  in a single campus — energized, not entitled.
                </p>
                <p className="text-sm leading-7 text-zinc-300">
                  Delta Flow cooling, gigawatt-scale power, and adaptive
                  infrastructure mean your deployment grows with your
                  ambition — not against your facility.
                </p>

                <div className="mt-6 border-t border-white/10 pt-5">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                    Trusted by
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {["Oracle Cloud", "Lambda", "Nutanix", "Enterprise & Gov"].map(
                      (name) => (
                        <span
                          key={name}
                          className="rounded-xl border border-white/10 bg-white/[0.07] px-3 py-1 text-xs text-zinc-300"
                        >
                          {name}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Customer profile cards — single column */}
          <div className="flex flex-col gap-3">
            {customerProfiles.map((item) => {
              const { Icon } = item;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Card className="rounded-2xl border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${item.iconBorder} ${item.bg} ${item.color}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="mb-1 text-sm font-bold text-zinc-900 dark:text-zinc-100">
                            {item.label}
                          </h4>
                          <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                            {item.blurb}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* -- CLOSING ---------------------------------------------- */}
        <section className="py-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-2xl border-0 bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 text-white shadow-2xl">
              <CardContent className="p-6">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-300/70">
                  The bottom line
                </p>
                <h2 className="mt-2 text-xl font-bold tracking-tight">
                  Your workload is evolving. Your infrastructure should too.
                </h2>
                <p className="mt-3 text-sm leading-7 text-blue-100/80">
                  Adaptive, sustainable, enterprise-grade infrastructure at
                  gigawatt scale. One partner. Every workload. No compromises.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["85 DCs", "5 GW", "50 Campuses", "100% Renewable", "Delta Flow"].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </div>

      {/* -- BACK TO TOP FAB --------------------------------------- */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-5 right-5 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-zinc-900 text-white shadow-lg active:scale-95 transition-transform dark:bg-white dark:text-zinc-900"
        aria-label="Back to top"
      >
        <ChevronUp className="h-5 w-5" />
      </button>
    </div>
  );
}
